import React, { useState, useEffect } from 'react';
import { listCategories } from '../data/listCategories';
import TitleBar from '../components/look-builder/TitleBar';
import Preview from '../components/look-builder/preview/Preview';
import List from '../components/look-builder/list/List';
import Gallery from '../components/look-builder/gallery/Gallery';
import { Container, Row, Column, Box, breakpoints, space } from '@generationtux/component-library';
import styled from 'styled-components';
import PreviewExploded from '../components/look-builder/preview/PreviewExploded';

const ColumnSticky = styled(Column)`
  text-align: center;
  @media (min-width: ${breakpoints['xs']}) {
    position: sticky;
    top: 0px;
    z-index: 500;
    pointer-events: none;
  }
`;

const ColumnRight = styled(Column)`
  & > div {
    max-width: ${props => (!props.activeCollection ? '550px' : 'none')};
  }
`;

const storeLook = (selectedCatalogNumbers, editId) => {
  const currentLooks =
    localStorage.getItem('localState') !== null ? JSON.parse(localStorage.getItem('localState'))['looks'] : [];
  if (editId !== null) {
    const newLook = {
      id: editId,
      look: selectedCatalogNumbers,
    };

    localStorage.setItem(
      'localState',
      JSON.stringify({
        version: process.env.REACT_APP_LOCAL_STORAGE_VERSION,
        looks: [...currentLooks.filter(x => x.id !== editId), newLook],
      })
    );
  } else {
    const newLook = {
      id: Math.floor(Math.random() * Math.floor(999999)),
      look: selectedCatalogNumbers,
    };
    localStorage.setItem(
      'localState',
      JSON.stringify({ version: process.env.REACT_APP_LOCAL_STORAGE_VERSION, looks: [...currentLooks, newLook] })
    );
  }
};

const LookBuilder = () => {
  const [activeCollection, setActiveCollection] = useState(null);
  const [selectedCatalogNumbers, setSelectedCatalogNumbers] = useState(listCategories);
  const [editId, setEditId] = useState(null);
  const [historyIndex, setHistoryIndex] = useState(null);
  const [history, setHistory] = useState([{ id: 1, data: selectedCatalogNumbers }]);
  const [previewExploded, setPreviewExploded] = useState(false);
  let mediaXs = window.matchMedia(`(min-width: ${breakpoints['xs']})`);
  let checkMediaXs = e => {
    if (e.matches) {
      setMediaQueryXsMatches(true);
    } else {
      setMediaQueryXsMatches(false);
    }
  };
  const [mediaQueryXsMatches, setMediaQueryXsMatches] = useState(mediaXs.matches);

  useEffect(() => {
    mediaXs.addListener(checkMediaXs);
    return () => {
      mediaXs.removeListener(checkMediaXs);
    };
  });

  useEffect(() => {
    if (localStorage.getItem('lookToEdit') !== null) {
      const editItem = JSON.parse(localStorage.getItem('lookToEdit'))['look'];
      setSelectedCatalogNumbers(editItem);
      setEditId(JSON.parse(localStorage.getItem('lookToEdit'))['id']);
      localStorage.removeItem('lookToEdit');
    }
  }, []);

  const docBrownsTimeMachine = (newlySelectedCatalogNumbers, addToTimeline, newHistoryIndex) => {
    // don't add undo/redo states to history
    if (!addToTimeline) {
      setHistoryIndex(newHistoryIndex);
    }

    if (addToTimeline) {
      // trim stale future history states
      const trimmedHistory = historyIndex !== null ? history.filter(x => x.id <= historyIndex) : history;

      // add new history states to timeline
      setHistory([...trimmedHistory, { id: trimmedHistory.length + 1, data: newlySelectedCatalogNumbers }]);
      setHistoryIndex(null);
    }

    // update new selected catalog numbers
    setSelectedCatalogNumbers(newlySelectedCatalogNumbers);
  };

  const undo = () => {
    let newIndex;
    let newCatalogNumbers;

    if (historyIndex === null && history.length > 1) {
      newIndex = history.length - 1;
      const newCatalogNumbers = history.filter(x => x.id === newIndex)[0].data;
      docBrownsTimeMachine(newCatalogNumbers, false, newIndex);
    }

    if (historyIndex - 1 > 0) {
      newIndex = historyIndex - 1;
      newCatalogNumbers = history.filter(x => x.id === newIndex)[0].data;
      docBrownsTimeMachine(newCatalogNumbers, false, newIndex);
    }
  };

  const redo = () => {
    let newIndex;
    let newCatalogNumbers;

    if (historyIndex === null) {
      newIndex = history.length;
      newCatalogNumbers = history.filter(x => x.id === newIndex)[0].data;
      docBrownsTimeMachine(newCatalogNumbers, false, newIndex);
    } else if (historyIndex + 1 <= history.length && history.length > 1) {
      newIndex = historyIndex + 1;
      newCatalogNumbers = history.filter(x => x.id === newIndex)[0].data;
      docBrownsTimeMachine(newCatalogNumbers, false, newIndex);
    }
  };

  return (
    <Box activeCollection={activeCollection}>
      {previewExploded ? (
        <PreviewExploded
          activeCollection={activeCollection}
          setActiveCollection={setActiveCollection}
          selectedCatalogNumbers={selectedCatalogNumbers}
          exploded={previewExploded}
          setExploded={setPreviewExploded}
          mediaQueryXsMatches={mediaQueryXsMatches}
        />
      ) : (
        <Container fluid style={{ maxWidth: 2240 }}>
          <Row alignItems="flex-start" flexWrap justifyContent="center">
            <ColumnSticky
              column={12}
              columnXs={5}
              columnSm={6}
              columnMd={5}
              columnLg={6}
              columnXl={5}
              py={[0, 4, 4, 4, 5, 5]}
            >
              <Preview
                history={history}
                selectedCatalogNumbers={selectedCatalogNumbers}
                undo={undo}
                redo={redo}
                previewExploded={previewExploded}
                setPreviewExploded={setPreviewExploded}
                isUndoable={history.length > 1 && historyIndex !== 1}
                isRedoable={historyIndex && historyIndex < history.length}
                activeCollection={activeCollection}
                mediaQueryXsMatches={mediaQueryXsMatches}
              />
            </ColumnSticky>
            <ColumnRight
              column={12}
              columnXs={7}
              columnSm={6}
              columnMd={activeCollection ? 7 : 6}
              columnLg={activeCollection ? 6 : 5}
              columnXl={activeCollection ? 7 : 5}
              pt={[activeCollection ? `calc(9vh + ${space[3]}px)` : 0, 4, 4, 4, 5, 5]}
              pb={4}
              style={{ transition: 'width .4s ease' }}
              activeCollection={activeCollection}
            >
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  {!activeCollection && (
                    <>
                      <TitleBar activeCollection={activeCollection} selectedCatalogNumbers={selectedCatalogNumbers} />
                      {/*                   
                    <Button
                    size="xs"
                    outline
                    buttonIcon="Swatch"
                    style={{backgroundColor:'transparent'}}
                    >
                      Order Swatches
                    </Button> */}
                    </>
                  )}
                </Box>
                {activeCollection ? (
                  <Gallery
                    selectedCatalogNumbers={selectedCatalogNumbers}
                    setSelectedCatalogNumbers={setSelectedCatalogNumbers}
                    docBrownsTimeMachine={docBrownsTimeMachine}
                    activeCollection={activeCollection}
                    setActiveCollection={setActiveCollection}
                    undo={undo}
                  />
                ) : (
                  <Box>
                    <List
                      selectedCatalogNumbers={selectedCatalogNumbers}
                      setSelectedCatalogNumbers={setSelectedCatalogNumbers}
                      docBrownsTimeMachine={docBrownsTimeMachine}
                      activeCollection={activeCollection}
                      setActiveCollection={setActiveCollection}
                      storeLook={storeLook}
                      editId={editId}
                    />
                  </Box>
                )}
              </Box>
            </ColumnRight>
          </Row>
        </Container>
      )}
    </Box>
  );
};

export default LookBuilder;
